import React from "react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import ViewDescription from "components/actions/ViewDescription";
import RecordStage from "lib/core_components/RecordStage";
import { findRegexMatches } from "lib/core_components/TextMatchHighlighter";
import { NoticeHighlights, useNoticeHighlight } from "lib/hooks/api/notices/useNoticeHighlight";
import { FeedPreviewRecord, PreviewMatchedSignal } from "lib/hooks/api/useFeedPreviewNotices";
import { RecordMatchInfo } from "lib/qued/queryRunner";
import { RecordPreview } from "lib/types/models";
import { escapeRegExp } from "lib/utils";
import { SelfServeQuery, SignalCategory } from "../../lib/StotlesApi";

dayjs.extend(relativeTime);

const SIGNAL_ORDER: SignalCategory[] = [
  SignalCategory.KEYWORD,
  SignalCategory.PARTNER,
  SignalCategory.COMPETITOR,
  SignalCategory.CPV_CODE,
];

// Turns the highlightNotice API's matched phrases into the field/start/end
// positions ViewDescription renders, reusing the app's own match logic.
function fieldMatches(
  text: string,
  phrases: readonly string[] | null | undefined,
  field: string,
): RecordMatchInfo["keyword_matches"] {
  if (!text || !phrases?.length) return [];
  return phrases.flatMap((phrase) => {
    if (!phrase) return [];
    const regexp = new RegExp(`\\b(${escapeRegExp(phrase)})\\b`, "gi");
    return findRegexMatches(text, regexp).map((m) => ({ field, start: m.start, end: m.end }));
  });
}

function toMatchInfo(record: RecordPreview, highlights: NoticeHighlights): RecordMatchInfo {
  return {
    keyword_matches: [
      ...fieldMatches(record.name, highlights?.title, "title"),
      ...fieldMatches(record.description ?? "", highlights?.description, "description"),
    ],
  };
}

type RecordCardProps = {
  record: RecordPreview;
  procurementStageId: string;
  matchedSignals: PreviewMatchedSignal[];
  highlightKeywords: string[];
  highlightOrgIds: string[];
};

function RecordCard({
  record,
  procurementStageId,
  matchedSignals,
  highlightKeywords,
  highlightOrgIds,
}: RecordCardProps): JSX.Element {
  // Only fetch keyword highlighting once the popover is opened — most cards are
  // never opened, so this avoids highlighting every notice on every edit.
  const [everOpened, setEverOpened] = React.useState(false);
  const { data: highlights } = useNoticeHighlight(
    {
      noticeId: procurementStageId,
      keywords: highlightKeywords,
      orgIds: highlightOrgIds,
      highlightSignals: false,
    },
    { enabled: everOpened },
  );
  const matchInfo = React.useMemo(
    () => (highlights ? toMatchInfo(record, highlights) : undefined),
    [highlights, record],
  );

  const orderedSignals = SIGNAL_ORDER.flatMap((category) =>
    matchedSignals.filter((signal) => signal.category === category),
  );
  return (
    <Card>
      <MatchReasonContainer>
        {orderedSignals.length > 0 && (
          <MatchList>
            {orderedSignals.map((signal) => (
              <MatchItem category={signal.category} key={signal.key}>
                {signal.name}
              </MatchItem>
            ))}
          </MatchList>
        )}
        <PublishDate>Published {dayjs(record.publish_date).fromNow()}</PublishDate>
      </MatchReasonContainer>
      <InfoContainer>
        <StageLabel stage={record.stage} />
        <Title>{record.name}</Title>
        <BuyerName>{record.buyer?.name}</BuyerName>
      </InfoContainer>
      <ActionContainer>
        <ViewDescription
          record={record}
          queryMatchInfo={matchInfo}
          onOpenChange={(open) => open && setEverOpened(true)}
          triggerLabel="Details"
        />
      </ActionContainer>
    </Card>
  );
}

type Props = {
  previewRecords: FeedPreviewRecord[];
  // The draft criteria, so an opened card can fetch its keyword highlighting.
  feedCriteria: SelfServeQuery;
};

function RecordCardList({ previewRecords, feedCriteria }: Props): JSX.Element {
  const highlightKeywords = feedCriteria.keywords?.keywords ?? [];
  const highlightOrgIds = [
    ...(feedCriteria.competitors?.org_ids ?? []),
    ...(feedCriteria.partners?.org_ids ?? []),
  ];

  return (
    <RecordList>
      {previewRecords.map(({ record, procurementStageId, matchedSignals }) => (
        <li key={record.guid}>
          <RecordCard
            record={record}
            procurementStageId={procurementStageId}
            matchedSignals={matchedSignals}
            highlightKeywords={highlightKeywords}
            highlightOrgIds={highlightOrgIds}
          />
        </li>
      ))}
    </RecordList>
  );
}

export default RecordCardList;

const RecordList = styled.ul({
  listStyle: "none",
  padding: 0,
  margin: "0 -16px -16px",
});

const Card = styled.div(({ theme }) => ({
  margin: 16,
  "&:first-of-type": { marginTop: 0 },
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 8,
  padding: 16,
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 8,
}));

const MatchReasonContainer = styled.div({
  width: "100%",
  display: "flex",
});

const PublishDate = styled.p(({ theme }) => ({
  fontSize: 12,
  lineHeight: "18px",
  color: theme.colors.sysTextSecondary,
  whiteSpace: "nowrap",
  minWidth: "min-content",
  margin: 0,
}));

const MatchList = styled.ul({
  flexGrow: 1,
  listStyle: "none",
  padding: 0,
  margin: "0 0 -8px",
});

const MatchItem = styled.li<{ category: SignalCategory }>(({ theme, category }) => {
  const backgrounds: Record<SignalCategory, string> = {
    [SignalCategory.KEYWORD]: theme.colors.sysSignalKeyword,
    [SignalCategory.CPV_CODE]: theme.colors.sysSignalKeyword,
    [SignalCategory.PARTNER]: theme.colors.sysSignalPartner,
    [SignalCategory.COMPETITOR]: theme.colors.sysSignalCompetitor,
  };
  return {
    fontSize: 14,
    lineHeight: "20px",
    color: theme.colors.sysTextDefault,
    padding: "1px 8px",
    marginRight: 8,
    marginBottom: 8,
    borderRadius: 2,
    display: "inline-block",
    backgroundColor: backgrounds[category],
  };
});

const InfoContainer = styled.div({
  width: "75%",
});

const StageLabel = styled(RecordStage)({
  marginRight: 8,
});

const Title = styled.div(({ theme }) => ({
  fontSize: 16,
  lineHeight: "24px",
  color: theme.colors.sysTextDefault,
  fontWeight: 400,
  margin: 0,
  display: "inline",
}));

const BuyerName = styled.p(({ theme }) => ({
  fontSize: 14,
  lineHeight: "22px",
  marginBottom: 0,
  color: theme.colors.sysTextDefault,
}));

const ActionContainer = styled.div({
  display: "flex",
  position: "relative",
  alignItems: "center",
});
